/**
 * Modal that captures a commit message before persisting an edited
 * Test Script / Test Case to the per-app git repo.
 *
 * Why: manual UI edits previously landed in the per-app git repo as
 * anonymous `ui-edit` commits with an auto-generated message
 * (`ui-edit: <name> (UI edit)`). For the version-history feature the
 * commits need to be attributable to a real operator and carry a
 * human-written reason, so the timeline tells a useful story.
 *
 * Shape:
 *   - Author is pre-filled from the signed-in user (read-only) — no
 *     "commit as someone else" affordance.
 *   - Commit message (subject line) is required, max ~70 chars by
 *     git convention. The dialog enforces non-blank; the backend
 *     re-validates.
 *   - Details (body) is optional, multi-line, blank-line separated
 *     from the subject when sent to git.
 *
 * Submission contract: `onSubmit({ message, details })` resolves
 * when the parent save succeeds (and the dialog closes), or throws
 * for the parent's existing error UI to render.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'

export interface SaveCommitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Pre-filled author identity (signed-in user). Shown read-only. */
  author: { name: string; email: string }
  /** Saves with the captured commit metadata. The parent owns the
   * actual API call and error surfacing. */
  onSubmit: (commit: { message: string; details?: string }) => Promise<void>
  /** Disables the Save button while the parent's save is in flight. */
  saving?: boolean
}

export function SaveCommitDialog({
  open,
  onOpenChange,
  author,
  onSubmit,
  saving = false,
}: SaveCommitDialogProps) {
  const [message, setMessage] = useState('')
  const [details, setDetails] = useState('')

  // Reset the fields whenever the dialog opens, so an earlier dismissed
  // message doesn't sneak into the next commit.
  useEffect(() => {
    if (open) {
      setMessage('')
      setDetails('')
    }
  }, [open])

  const subject = message.trim()
  const canSubmit = subject.length > 0 && !saving

  async function handleSubmit() {
    if (!canSubmit) return
    await onSubmit({
      message: subject,
      details: details.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Save changes</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Author</Label>
            <div className="text-sm">
              {author.name}{' '}
              <span className="text-muted-foreground">&lt;{author.email}&gt;</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="commit-message">
              Commit message <span className="text-destructive">*</span>
            </Label>
            <Input
              id="commit-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Short description of what changed"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="commit-details">Details (optional)</Label>
            <Textarea
              id="commit-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Why the change, or additional context"
              rows={4}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
